/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { setProductAdded } from '../../../../../actions/my-puratos/alerts.action';
import { addItemsToCart } from '../../../../../actions/my-puratos/myProducts.action';
import { WithRole } from '../../../hoc/withRole';
import { RoleMatrix } from '../../../../../utils/my-puratos/roleUtils';
import {
    getCartQuantity,
    selectSKUBundle,
} from '../../../../../selectors/my-puratos/cart';
import {
    PromotionsTypeEnum,
    SKU,
} from '../../../../../data/my-puratos/mypuratos';
import { ShowHide } from '../../../electrons/ShowHide';
import BundleRow from './BundleRow';
import { EMPTY_OBJECT } from '../../../../../utils/my-puratos';
import {
    addToCartAction,
    isLoadingById,
} from '../../../../../selectors/my-puratos/loader';
import { MobileOnly, Table } from '../../../atoms/table/Table';
import {
    AddToCartCell,
    CELL_SIZE,
    ObsoleteCell,
    PackagingValueCell,
    ProductDescriptionCell,
    ProductImageCell,
    ProductPromoCell,
    QuantityInputCell,
    UnitPriceValueCell,
    ValueCell,
    WeightPriceValueCell,
} from '../../../atoms/table/Cells';
import { GTMContext } from '../../../../../analytics/ga4/utils/GTMContext';
import { StockInformation } from '../../../../atoms/details/StockInformation';
import { addProductClickEvent } from '../../../../../utils/customEventHandler';
import {
    selectMyPuratosAccountEmail,
    selectUserId,
} from '../../../../../selectors/auth';
import {
    getCurrencyCode, getSubTotalPrice,
} from '../../../../../selectors/cart';
import { GA4LoginType } from '../../../../../analytics/ga4/types/login';
import { GA4PageType } from '../../../../../analytics/ga4/types/page';
import { googleEvents } from '../../../../../utils/googleEvents';
import { getLanguageIso } from '../../../../../selectors/generic';
import {
    productEventParseAdd,
    productEventParseSelect,
} from '../../../../../analytics/ga4/parsers/item';
import { GA4InteractionType } from '../../../../../analytics/ga4/types/interaction';
import { isLoggedIn } from '../../../../../data/user.model';
import { interactionPageMapper, interactionCrossSellMapper } from '../../../../../analytics/ga4/utils/mapper';
import { setProductCrossSell } from '../../../../../actions/analytics';

export const renderPackagePrice = (
    product,
    homePageModel,
    i18n,
    isObsolete,
    isBundle,
    showPrice
) => {
    return (
        <>
            {isObsolete ? (
                <ObsoleteCell
                    valign={'center'}
                    align={'center'}
                    size={CELL_SIZE._auto}
                    text={homePageModel.obsoleteSku}
                />
            ) : (
                <>
                    <PackagingValueCell
                        valign={'center'}
                        align={'left'}
                        size={CELL_SIZE._equal}
                        product={product}
                        i18n={i18n}
                    />
                    <WithRole roles={[RoleMatrix.puratosCustomerValidated]}>
                        {showPrice ? (
                            <>
                                <WeightPriceValueCell
                                    valign={'center'}
                                    align={'left'}
                                    i18n={i18n}
                                    size={CELL_SIZE._equal}
                                    product={product}
                                    isBundle={isBundle}
                                />
                                <UnitPriceValueCell
                                    valign={'center'}
                                    align={'left'}
                                    i18n={i18n}
                                    size={CELL_SIZE._equal}
                                    product={product}
                                    isBundle={isBundle}
                                />
                            </>
                        ) : (
                            <>
                                <ValueCell size={CELL_SIZE._equal} />
                                <ValueCell size={CELL_SIZE._equal} />
                            </>
                        )}
                    </WithRole>
                </>
            )}
        </>
    );
};

class ProductRow extends Component {
    static propTypes = {
        data: PropTypes.shape(SKU),
    };

    static contextType = GTMContext;

    state = {
        item: {
            ...this.props.data,
            quantity: this.props.staticQuantity
                ? this.props.staticQuantity
                : Math.max(
                      1,
                      this.props.data.minimumOrderableQuantity -
                          (this.props.cartQuantity || 0)
                  ) || 1,
        },
        response: false,
    };

    updateQuantity = ({ item: { quantity = 1 } }) => {
        this.setState({
            item: { ...this.state.item, quantity: quantity },
        });
    };

    handleSubmission = (success) => {
        this.setState({
            item: { ...this.state.item, quantity: 1 },
            response: success,
        });
    };

    addToCartGAHandler = () => {
        const { item } = this.state;
        const {
            userId,
            userEmail,
            subTotalPrice,
            currency,
            setProductCrossSell,
        } = this.props;

        setProductCrossSell({
            ...item,
            type: interactionCrossSellMapper(this.context),
        });

        const promotionData = {
            promotion_id: item ? item.promoCode : '',
            promotion_name: item ? item.longPromoText : '',
        };

        googleEvents.addToCart({
            user_id: userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? userEmail : '',
            login_status: userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
            website_section: interactionPageMapper(this.context),
            subTotalPrice: subTotalPrice,
            currency: currency,
            items: productEventParseAdd(
                item,
                currency,
                interactionPageMapper(this.context),
                null,
                promotionData
            ),
        });
    };

    addItemToCart = (messages) => {
        const {
            addToCartAction,
            defaultAddToCartAction,
            setProductAdded,
        } = this.props;
        const { item } = this.state;

        this.setState({ response: false }, () => {
            setProductAdded(item);
            if (item.quantity > 0) {
                (addToCartAction || defaultAddToCartAction)({
                    items: [item],
                    alerts: messages,
                    reset: this.handleSubmission,
                    type: this.context,
                    addToCartGA: this.addToCartGAHandler,
                });
            }
        });
    };

    onClickProduct = (product, position) => {
        addProductClickEvent(
            product,
            this.context,
            false,
            this.props.userId,
            this.props.currency,
            position
        );
        googleEvents.selectItem({
            user_id: this.props.userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? this.props.userEmail : '',
            login_status: this.props.userId
                ? GA4LoginType.LOGGED_IN
                : GA4LoginType.GUEST,
            website_section: GA4PageType.allProducts,
            items: productEventParseSelect(
                product,
                this.props.currency,
                GA4InteractionType.allProducts
            ),
        });
    };

    render() {
        const {
            data: product,
            i18n,
            wide,
            titlesClickable,
            homePageModel,
            ignoreBundles,
            loading,
            hidden,
            serviceLabel,
            serviceTag,
            position,
            isStatic = false,
            isMobile = false,
        } = this.props;

        if (hidden) {
            return null;
        }

        const bundle = selectSKUBundle(product);

        return (
            <React.Fragment>
                <ShowHide
                    show={
                        !bundle ||
                        bundle.type !== PromotionsTypeEnum.MANDATORY_BUNDLE
                    }
                >
                    <Table.Row>
                        <ProductPromoCell
                            valign={'center'}
                            align={'left'}
                            size={CELL_SIZE._auto}
                            product={product}
                        />
                        <ProductImageCell
                            valign={'top'}
                            align={'center'}
                            size={CELL_SIZE._100}
                            product={product}
                            showCartAmount
                            fallbackImage={homePageModel.fallbackImage}
                            titlesClickable={titlesClickable}
                            onClick={() =>
                                this.onClickProduct(product, position)
                            }
                        />
                        <ProductDescriptionCell
                            size={CELL_SIZE._equalLg}
                            i18n={i18n}
                            product={product}
                            serviceLabel={serviceLabel}
                            serviceTag={serviceTag}
                            productAmount={
                                isStatic && isMobile
                                    ? this.state.item?.quantity
                                    : null
                            }
                            titlesClickable={titlesClickable}
                            onClick={() =>
                                this.onClickProduct(product, position)
                            }
                        />
                        {renderPackagePrice(
                            product,
                            homePageModel,
                            i18n,
                            product.isObsolete,
                            false,
                            product.hasPrice
                        )}
                        {product.hasPrice && (
                            <MobileOnly>
                                <StockInformation
                                    type={product.stockType}
                                    className={'mobile-row'}
                                />
                            </MobileOnly>
                        )}
                        {(!isStatic || (isStatic && !isMobile)) && (
                            <WithRole
                                roles={[RoleMatrix.puratosCustomerValidated]}
                            >
                                <Table.MobileSeparator style={'dashed'} />
                                <QuantityInputCell
                                    valign={'center'}
                                    align={'center'}
                                    size={CELL_SIZE._125}
                                    i18n={i18n}
                                    product={this.state.item}
                                    modifiable
                                    minimumQuantity={Math.max(
                                        1,
                                        product.minimumOrderableQuantity -
                                            (this.props.cartQuantity || 0)
                                    )}
                                    updateItem={this.updateQuantity}
                                    disableInput={loading || product.isObsolete}
                                    noControls={isStatic}
                                />
                                {isStatic ? (
                                    <Table.Cell
                                        valign={'center'}
                                        align={'right'}
                                        className={'cell__add-to-cart'}
                                        size={CELL_SIZE._75}
                                        sizeMobile={CELL_SIZE._150}
                                    >
                                        <span className="svg-check-mark-green-circle-xxl" />
                                    </Table.Cell>
                                ) : (
                                    <AddToCartCell
                                        valign={'center'}
                                        align={'right'}
                                        size={CELL_SIZE._75}
                                        sizeMobile={CELL_SIZE._150}
                                        response={this.state.response}
                                        loading={loading}
                                        disabled={
                                            loading ||
                                            product.stockType === 'BLOCKSALE' ||
                                            product.isObsolete
                                        }
                                        handleClick={this.addItemToCart}
                                    />
                                )}
                            </WithRole>
                        )}
                    </Table.Row>
                </ShowHide>
                <ShowHide show={!ignoreBundles && bundle}>
                    <BundleRow
                        data={bundle}
                        i18n={i18n}
                        titleClickable={true}
                        disabledInput={true}
                        showPrice={product.hasPrice && !product.isObsolete}
                        wide={wide}
                    />
                </ShowHide>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    homePageModel: state.myPuratos_aem.homepage || EMPTY_OBJECT,
    cartQuantity: getCartQuantity(
        state,
        ownProps.data && ownProps.data.materialNumber
    ),
    loading: isLoadingById(
        state,
        addToCartAction(ownProps.data.materialNumber)
    ),
    userId: selectUserId(state),
    currency: getCurrencyCode(state),
    userEmail: selectMyPuratosAccountEmail(state),
    subTotalPrice: getSubTotalPrice(state),
});

const mapDispatchToProps = {
    setProductAdded,
    defaultAddToCartAction: addItemsToCart,
    setProductCrossSell,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductRow);
